import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmtCur, fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import { ClipboardText, FloppyDisk, Plus, Info, User } from "@phosphor-icons/react";

const BORDER = "1px solid var(--omh-border)";
const MUTED = "var(--omh-text-muted)";
const TEAL = "#0d9488";
const EDIT_ROLES = ["admin", "super_admin", "manager", "po_dispatch"];
const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");

const th = { textAlign: "right", padding: "9px 10px", fontSize: 11.5, textTransform: "uppercase",
  letterSpacing: 0.4, color: MUTED, fontWeight: 700, whiteSpace: "nowrap", borderBottom: BORDER, background: "#f8fafc" };
const td = { padding: "6px 8px", fontSize: 13, borderBottom: BORDER, whiteSpace: "nowrap" };

export default function OtherForecastNew() {
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.includes(user?.role);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [tpl, setTpl] = useState(null);       // {currency, months, items, saved, rev, updated_by, updated_at}
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/forecasts/other/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => setCustomers([]));
  }, []);

  const loadTemplate = (name) => {
    if (!name) { setTpl(null); setRows([]); return; }
    setLoading(true);
    api.get(`/forecasts/other/template?customer=${encodeURIComponent(name)}`)
      .then((r) => {
        setTpl(r.data);
        setRows((r.data.items || []).map((it) => ({ ...it, months: { ...it.months } })));
      })
      .catch(() => { toast.error("Failed to load parts for this customer"); setTpl(null); setRows([]); })
      .finally(() => setLoading(false));
  };

  const onPickCustomer = (name) => { setCustomer(name); loadTemplate(name); };

  const months = tpl?.months || [];
  const currency = tpl?.currency || "USD";

  const setQty = (idx, code, val) => {
    setRows((prev) => {
      const next = [...prev];
      const m = { ...next[idx].months, [code]: val === "" ? "" : Number(val) };
      next[idx] = { ...next[idx], months: m };
      return next;
    });
  };
  const setField = (idx, field, val) => {
    setRows((prev) => { const n = [...prev]; n[idx] = { ...n[idx], [field]: val }; return n; });
  };
  const addManualRow = () => {
    setRows((prev) => [{ item: "", item_desc: "", price: 0, avg6: 0, source: "manual",
      months: Object.fromEntries(months.map((m) => [m.code, ""])) }, ...prev]);
  };
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const rowTotal = (r) => months.reduce((s, m) => s + (Number(r.months[m.code]) || 0), 0);
  const grand = useMemo(() => {
    let q = 0, v = 0;
    rows.forEach((r) => { const t = rowTotal(r); q += t; v += t * (Number(r.price) || 0); });
    return { q, v };
  }, [rows, months]);

  const save = async () => {
    const items = rows
      .filter((r) => (r.item || "").trim())
      .map((r) => ({
        item: r.item.trim(), item_desc: r.item_desc || "", price: Number(r.price) || 0,
        price_source: r.price_source || "", avg6: Number(r.avg6) || 0, source: r.source || "manual",
        months: Object.fromEntries(months.map((m) => [m.code, Number(r.months[m.code]) || 0])),
      }));
    if (!items.some((it) => Object.values(it.months).some((q) => q > 0))) {
      toast.error("Enter at least one forecast quantity before saving.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/forecasts/other/save", {
        customer_name: customer, currency, horizon: months.map((m) => m.code), items,
      });
      toast.success(`Forecast saved · rev ${r.data.rev} · ${r.data.item_count} item${r.data.item_count === 1 ? "" : "s"}`);
      loadTemplate(customer);
      setCustomers((prev) => prev.map((c) => c.name === customer
        ? { ...c, has_forecast: true, rev: r.data.rev, updated_at: r.data.updated_at, updated_by: r.data.updated_by } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const monthCol = { ...th, minWidth: 78 };

  return (
    <Layout>
      <div data-testid="other-fc-new-page" style={{ padding: "12px 26px 22px", maxWidth: 1500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, display: "flex", alignItems: "center", gap: 10, margin: 0, color: "var(--omh-text)" }}>
            <ClipboardText size={22} weight="duotone" color={TEAL} /> Other Customers — New Forecast
          </h1>
          {tpl && canEdit && (
            <button data-testid="other-fc-save-btn" disabled={saving} onClick={save}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", border: "none", borderRadius: 9,
                background: saving ? "#94a3b8" : TEAL, color: "#fff", fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer" }}>
              <FloppyDisk size={17} weight="bold" /> {saving ? "Saving…" : "Save forecast"}
            </button>
          )}
        </div>

        {/* Customer picker */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: MUTED }}>Customer</label>
          <select data-testid="other-fc-customer-select" value={customer} onChange={(e) => onPickCustomer(e.target.value)}
            style={{ padding: "9px 12px", border: BORDER, borderRadius: 9, fontSize: 13.5, background: "#fff", minWidth: 320 }}>
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.name} value={c.name}>{c.name}{c.has_forecast ? `  · saved (rev ${c.rev})` : ""}</option>
            ))}
          </select>
          {tpl?.saved && (
            <span data-testid="other-fc-saved-meta" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", padding: "4px 10px", borderRadius: 20 }}>
              <User size={13} weight="bold" /> Last saved by {tpl.updated_by} · {fmtDateTime(tpl.updated_at)} · rev {tpl.rev}
            </span>
          )}
        </div>

        {!canEdit && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", padding: "8px 12px", borderRadius: 8 }}>
            You have view-only access. Ask an admin, manager, or PO/Dispatch user to edit forecasts.
          </div>
        )}

        {loading ? (
          <div style={{ padding: 50, textAlign: "center", color: MUTED }}>Loading parts…</div>
        ) : !tpl ? (
          <div data-testid="other-fc-empty" style={{ marginTop: 26, padding: 44, textAlign: "center", border: "1px dashed var(--omh-border)", borderRadius: 14, background: "#fff" }}>
            <ClipboardText size={38} weight="duotone" color="#94a3b8" />
            <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 10 }}>Pick a customer to build a forecast</div>
            <div style={{ color: MUTED, fontSize: 13, marginTop: 5 }}>Parts shipped in the last 6 months (plus open-order parts) load automatically.</div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: MUTED }}>
                <Info size={14} /> <b style={{ color: "var(--omh-text)" }}>Avg/mo</b> = indicative average of the last 6 months shipped (read-only). Enter forecast quantities for the next 6 months.
              </div>
              {canEdit && (
                <button data-testid="other-fc-add-part-btn" onClick={addManualRow}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", border: `1px solid ${TEAL}`, borderRadius: 8, background: "#fff", color: TEAL, fontWeight: 700, fontSize: 12.5, cursor: "pointer", marginLeft: "auto" }}>
                  <Plus size={14} weight="bold" /> Add part
                </button>
              )}
            </div>

            <div style={{ marginTop: 12, background: "#fff", border: BORDER, borderRadius: 12, overflow: "auto" }}>
              <table data-testid="other-fc-grid" style={{ borderCollapse: "collapse", minWidth: 980, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: "left", position: "sticky", left: 0, zIndex: 2 }}>Item</th>
                    <th style={{ ...th, textAlign: "left" }}>Description</th>
                    <th style={{ ...th, background: "#eff6ff", color: "#1d4ed8" }} title="Indicative avg monthly demand, last 6 months">Avg/mo</th>
                    <th style={th}>Price</th>
                    {months.map((m) => <th key={m.code} style={monthCol}>{m.label}</th>)}
                    <th style={{ ...th, background: "#ecfdf5", color: TEAL }}>Total</th>
                    {canEdit && <th style={{ ...th, textAlign: "center" }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={months.length + 5} style={{ ...td, textAlign: "center", color: MUTED, padding: 26 }}>No parts. Use “Add part”.</td></tr>
                  )}
                  {rows.map((r, i) => {
                    const isManual = r.source === "manual";
                    const rt = rowTotal(r);
                    return (
                      <tr key={i} data-testid="other-fc-row" style={{ background: i % 2 ? "#fbfdff" : "#fff" }}>
                        <td style={{ ...td, textAlign: "left", fontFamily: "Inter, sans-serif", fontWeight: 700, position: "sticky", left: 0, background: i % 2 ? "#fbfdff" : "#fff", zIndex: 1 }}>
                          {isManual && !r._locked ? (
                            <input data-testid="other-fc-item-input" value={r.item} onChange={(e) => setField(i, "item", e.target.value)} placeholder="Item code"
                              style={{ width: 110, padding: "5px 7px", border: BORDER, borderRadius: 6, fontSize: 12.5, fontFamily: "inherit" }} />
                          ) : r.item}
                          {r.source && <span title={r.source} style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: r.source === "invoice" ? "#0f766e" : r.source === "open" ? "#b45309" : "#7c3aed", textTransform: "uppercase" }}>{r.source === "invoice" ? "hist" : r.source}</span>}
                        </td>
                        <td style={{ ...td, textAlign: "left", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {isManual ? (
                            <input value={r.item_desc} onChange={(e) => setField(i, "item_desc", e.target.value)} placeholder="Description"
                              style={{ width: 220, padding: "5px 7px", border: BORDER, borderRadius: 6, fontSize: 12.5 }} />
                          ) : (r.item_desc || "—")}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: "#1d4ed8", background: "#f8fbff", fontWeight: 600 }} title="Indicative — last 6 months average">{fmtNum(r.avg6)}</td>
                        <td style={{ ...td, textAlign: "right", color: MUTED }}>
                          {isManual ? (
                            <input type="number" step="0.01" value={r.price ?? ""} onChange={(e) => setField(i, "price", e.target.value)}
                              style={{ width: 74, padding: "5px 6px", border: BORDER, borderRadius: 6, fontSize: 12.5, textAlign: "right" }} />
                          ) : (r.price ? fmtCur(r.price, currency) : "—")}
                        </td>
                        {months.map((m) => (
                          <td key={m.code} style={{ ...td, textAlign: "right", padding: "4px 6px" }}>
                            <input data-testid={`other-fc-qty-${m.code}`} type="number" min="0" disabled={!canEdit}
                              value={r.months[m.code] ?? ""} onChange={(e) => setQty(i, m.code, e.target.value)}
                              style={{ width: 66, padding: "6px 7px", border: BORDER, borderRadius: 6, fontSize: 12.5, textAlign: "right",
                                background: canEdit ? "#fff" : "#f1f5f9" }} />
                          </td>
                        ))}
                        <td style={{ ...td, textAlign: "right", fontWeight: 800, background: "#f0fdf4", color: "#15803d" }}>{fmtNum(rt)}</td>
                        {canEdit && (
                          <td style={{ ...td, textAlign: "center" }}>
                            <button data-testid="other-fc-remove-row" onClick={() => removeRow(i)} title="Remove row"
                              style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 15, fontWeight: 700 }}>×</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#ecfdf5" }}>
                      <td style={{ ...td, fontWeight: 800, position: "sticky", left: 0, background: "#ecfdf5" }}>Total</td>
                      <td style={td} colSpan={2} />
                      <td style={td} />
                      {months.map((m) => {
                        const q = rows.reduce((s, r) => s + (Number(r.months[m.code]) || 0), 0);
                        return <td key={m.code} style={{ ...td, textAlign: "right", fontWeight: 800 }}>{fmtNum(q)}</td>;
                      })}
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, color: TEAL }}>{fmtNum(grand.q)}</td>
                      {canEdit && <td style={td} />}
                    </tr>
                    <tr style={{ background: "#ecfdf5" }}>
                      <td style={{ ...td, fontWeight: 700, color: MUTED, position: "sticky", left: 0, background: "#ecfdf5" }}>Value ({currency})</td>
                      <td style={td} colSpan={2} />
                      <td style={td} />
                      {months.map((m) => {
                        const v = rows.reduce((s, r) => s + (Number(r.months[m.code]) || 0) * (Number(r.price) || 0), 0);
                        return <td key={m.code} style={{ ...td, textAlign: "right", fontSize: 11.5, color: TEAL }}>{v ? fmtCur(v, currency) : "—"}</td>;
                      })}
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, color: TEAL }}>{grand.v ? fmtCur(grand.v, currency) : "—"}</td>
                      {canEdit && <td style={td} />}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
